import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

type RevisionRoundLineAreaChartData = {
  rounds: Array<{
    round: string;
    count: number;
  }>;
};

interface Props {
  data: RevisionRoundLineAreaChartData;
  axes?: {
    x?: string;
    y?: string;
  };
}

export const RevisionRoundLineAreaChart = ({ data, axes }: Props) => {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const xAxisHeight = isMobile ? 44 : 30;
  const chartHeight = isMobile ? (axes?.x ? 248 : 272) : axes?.x ? 276 : 300;

  return (
    <Box>
      <Flex align="stretch">
        {axes?.y ? (
          <Flex align="center" justify="center" pr={2} minW="28px">
            <Text
              textStyle="textContext"
              textAlign="center"
              sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {axes.y}
            </Text>
          </Flex>
        ) : null}
        <Box flex="1">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart
              data={data.rounds}
              margin={{
                top: 16,
                right: isMobile ? 8 : 16,
                left: isMobile ? 0 : 16,
                bottom: isMobile ? 12 : 8
              }}
            >
              <defs>
                <linearGradient
                  id="revisionRoundArea"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="4 4"
                stroke="#D9E2EC"
                vertical={false}
              />
              <XAxis
                dataKey="round"
                axisLine={false}
                tickLine={false}
                height={xAxisHeight}
                interval={0}
                tickMargin={isMobile ? 10 : 6}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={isMobile ? 32 : 40}
                tick={{ fill: '#334155', fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 12,
                  border: '1px solid #CBD5E1',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)',
                  backgroundColor: '#FFFFFF'
                }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#7C3AED"
                strokeWidth={3}
                fill="url(#revisionRoundArea)"
                dot={isMobile ? false : { r: 4, fill: '#7C3AED' }}
                activeDot={{
                  r: 6,
                  fill: '#FFFFFF',
                  stroke: '#7C3AED',
                  strokeWidth: 2
                }}
                isAnimationActive
                animationDuration={1100}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
      {axes?.x ? (
        <Text mt={2} textAlign="center" textStyle="textContext">
          {axes.x}
        </Text>
      ) : null}
    </Box>
  );
};
